/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import InputMask from "react-input-mask";
import { useTranslation } from 'react-i18next';

import "./RateProduct.scss";
import backButton from "./left_arrow.png";
import closeButton from "./close_button.png";
import loading from "./loading-inverse.gif";

import { StarsRating } from "./StarsRating";
import { Ripple } from "./Ripple";
import useQuery from "./useQuery";

// Parser para categorias estruturadas (ex: "Origem:[Sao Paulo]" ou "Viação[Santa Cruz]")
const parseCategoryMap = (categories) => {
  if (!Array.isArray(categories)) return {};
  
  const categoryMap = {};
  categories.forEach(cat => {
    if (typeof cat !== 'string') return;
    
    // Regex para capturar: Chave:[Valor] ou Chave[Valor]
    const match = cat.match(/^([^:\[]+):?\[([^\]]+)\]$/);
    if (match) {
      const key = match[1].trim();
      const value = match[2].trim();
      categoryMap[key] = value;
    }
  });
  
  return categoryMap;
};

// Helper para interpolar variáveis nos textos customizados
const interpolateText = (text, variables, categoryMap = {}) => {
  if (!text) return text;
  
  let result = text;
  
  // Primeiro: interpolar category['Key'] com valores do mapa estruturado
  Object.keys(categoryMap).forEach(key => {
    const placeholder = `{{category['${key}']}}`;
    const placeholderAlt = `{{category["${key}"]}}`;
    result = result.replace(new RegExp(placeholder.replace(/[{}[\]']/g, '\\$&'), 'g'), categoryMap[key] || '');
    result = result.replace(new RegExp(placeholderAlt.replace(/[{}[\]"]/g, '\\$&'), 'g'), categoryMap[key] || '');
  });
  
  // Depois: interpolar variáveis simples
  Object.keys(variables).forEach(key => {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), variables[key] || '');
  });
  
  return result;
};

export function RateProduct(props) {
  const { t } = useTranslation();
  const { show, product, customerInfo, initialRating, external, embedded, preview } = props;
  const onHideProps = props.onHide;
  const onComplete = props.onComplete;
  const { modal } = props;
  const getNextItem = props.getNextItem;
  const onNavigateNext = props.onNavigateNext;
  const [rating, setRating] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [explicitConsent, setExplicitConsent] = useState(false);
  const [isLoadingCompleteness, setIsLoadingCompleteness] = useState(false);
  const [attributesSent, setAttributesSent] = useState(false);
  const [isSendEnabled, setIsSendEnabled] = useState(false);
  const [isSendAttributesEnabled, setIsSendAttributesEnabled] = useState(false);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [recommended, setRecommended] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [idReview, setIdReview] = useState(null);
  const [updateTokenId, setUpdateTokenId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  
  // Estados para modo progressivo de atributos
  const [currentAttributeIndex, setCurrentAttributeIndex] = useState(0);
  const [isAttributeTransitioning, setIsAttributeTransitioning] = useState(false);
  const [savedAttributes, setSavedAttributes] = useState([]);

  const query = useQuery();
  const starsRef = useRef();
  const timerDebounce = useRef();
  const [topics, setTopics] = useState([]);
  const [topicsCompleteness, setTopicsCompleteness] = useState([]);

  // Variáveis para interpolação nos textos customizados
  const productData = product?.product || {};
  
  // Processa arrays para texto (ex: categories)
  const processedProductData = Object.keys(productData).reduce((acc, key) => {
    const value = productData[key];
    if (Array.isArray(value)) {
      // Para arrays, cria duas versões: plural (join) e singular (primeiro item)
      acc[key] = value.join(', '); // Ex: categories = "Cat1, Cat2, Cat3"
      const singularKey = key.endsWith('ies') 
        ? key.slice(0, -3) + 'y'  // categories -> category
        : key.endsWith('s') 
          ? key.slice(0, -1)       // tags -> tag
          : key;
      acc[singularKey] = value[0] || ''; // Ex: category = "Cat1"
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

  // Parse de categorias estruturadas (ex: "Origem:[São Paulo]")
  const categoryMap = parseCategoryMap(productData.categories);

  const interpolationVars = {
    // Campos do produto processados
    ...processedProductData,
    // Aliases para compatibilidade
    productName: product?.product?.name ?? t('product'),
    name: product?.product?.name ?? t('product'),
    storeName: product?.name ?? customerInfo?.name ?? '',
    // Campos específicos da loja
    storeUrl: customerInfo?.storeUrl ?? ''
  };

  const getTopics = async () => {
    const result = await axios.get(
      `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/topics`
    );
    const topicsResult = result.data.topics
      ? result.data.topics.slice(0, 3).map((item) => item.title)
      : [];
    setTopics(topicsResult);
  };

  function debounce(method, delay) {
    if (timerDebounce.current) clearTimeout(timerDebounce.current);
    timerDebounce.current = setTimeout(function () {
      method();
    }, delay);
  }

  useEffect(() => {
    if (!show) {
      starsRef && starsRef.current && starsRef.current.clear();
      setRating(0);
      setText("");
      setFiles([]);
      setImages([]);
      setActiveStep(1);
      setAvailableAttributes([]);
      setIdReview(null);
      setName("");
      setPhone("");
      setVideo(null);
      setUploadError(null);
      setCurrentAttributeIndex(0);
      setSavedAttributes([]);
      setAttributesSent(false);
    } else {
      if (initialRating)
        starsRef &&
          starsRef.current &&
          starsRef.current.setRating(initialRating);

      window.gtag("event", "rateProduct", {
        customer: product?.customer,
        sku: product?.sku,
      });

      getTopics();
    }
  }, [show]);

  useEffect(() => {
    if (!modal) {
      getTopics();
    }
  }, []);

  useEffect(() => {
    setIsSendAttributesEnabled(attributeValues.length > 0 && !isLoading);
  }, [attributeValues, isLoading]);

  useEffect(() => {
    debounce(() => {
      checkCompleteness(text);
    }, 1500);
  }, [text]);


  useEffect(() => {
    const requireComment = customerInfo?.settings?.collectionInterface?.requireComment || 
                          customerInfo?.settings?.requireText || 
                          false;
    const enableRecommendation = customerInfo?.settings?.collectionInterface?.enableRecommendation !== false;
    
    setIsSendEnabled(
      rating > 0 &&
        (!requireComment || text !== "") &&
        (!enableRecommendation || recommended !== "") &&
        (preview || product.orderId ||
          (name !== "" &&
            name.length >= 3 &&
            phone !== "" &&
            phone.length === 13)) &&
        !isLoading &&
        (
          (!customerInfo?.settings?.explicitConsentAllReviews &&
            (images.length === 0 || !customerInfo?.settings?.explicitConsent || explicitConsent)) ||
          (customerInfo?.settings?.explicitConsentAllReviews && explicitConsent)
        )
    );
  }, [rating, recommended, isLoading, name, phone, explicitConsent, text, images, images.length, customerInfo]);

  useEffect(() => {
    if (rating > 0)
      window.gtag("event", "setRating", {
        customer: product?.customer,
        sku: product?.sku,
        rating,
      });

    if (rating >= 3) setRecommended(true);
    else if (rating > 0) setRecommended(false);
    else setRecommended("");
  }, [rating]);

  const checkCompleteness = async (text) => {
    const data = {
      topics,
      text,
    };

    if (product)
      window.gtag("event", "changeText", {
        customer: product?.customer,
        sku: product?.sku,
        textLength: text.length,
        hasTopics: topics.length > 0,
      });

    if (text.trim() === "" || text.trim().length < 10 || topics.length === 0) {
      setTopicsCompleteness([]);
      return;
    }

    setIsLoadingCompleteness(true);
    var result = await axios.post(
      `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/completeness`,
      data
    );

    setTopicsCompleteness(result.data);
    setIsLoadingCompleteness(false);
  };

  const previewFiles = (evt) => {
    const file = evt.target.files[0];

    if (file.type.includes("image")) {
      window.gtag("event", "sendPicture", {
        customer: product?.customer,
        sku: product?.sku,
      });

      setFiles([...files, evt.target.files[0]]);
      setImages([...images, URL.createObjectURL(file)]);

      setUploadError(null);
    } else if (video == null) {
      window.gtag("event", "sendVideo", {
        customer: product?.customer,
        sku: product?.sku,
      });

      const url = URL.createObjectURL(file);
      console.log("url", url);

      setFiles([...files, evt.target.files[0]]);
      setVideo(url);

      setUploadError(null);
    } else {
      setUploadError(t('errors.video_limit'));
    }

    evt.target.files = null;
    evt.target.value = "";
  };

  const onHide = () => {
    window.gtag("event", "cancelRating", {
      customer: product?.customer,
      sku: product?.sku,
    });
    if (
      activeStep !== 1 ||
      window.confirm(t('confirm_cancel'))
    )
      onHideProps();
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();

    if (!isSendEnabled) return;

    if (preview) {
      alert('Modo pré-visualização — nenhuma avaliação será registrada.');
      return;
    }

    setIsLoading(true);

    window.gtag("event", "sendReview", {
      customer: product?.customer,
      sku: product?.sku,
      hasText: text.length > 0,
      hasPicture: images.length > 0,
      hasVideo: video != null,
      hasTopics: topics.length > 0,
      textLength: text.length,
      topicsCompleteness:
        topicsCompleteness.filter((x) => !!x).length / topics.length,
    });

    try {
      let data = {
        orderId: product.orderId,
        verificationTokenId: !external ? product._id : null,
        externalItemId: external ? product._id : null,
        name: name,
        phone: phone,
        rating: rating,
        text: text,
        recommended: recommended,
        userId: product.userId,
        source: !external ? query.get("source") ?? "web" : "external",
        notificationId: query.get("notificationId")?.split("?")[0] || undefined,
      };

      let result = await axios.post(
        `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review`,
        data
      );

      if (result && result.data) {
        var {
          updateToken,
          _id: reviewId,
          availableAttributes: attributes,
          coupon,
        } = result.data;
        setAvailableAttributes(attributes);
        setUpdateTokenId(updateToken);
        setIdReview(reviewId);

        // Notifica o componente pai e passa o cupom (se houver)
        if (onComplete) onComplete(coupon);

        if (images.length > 0) {
          const uploadedKeys = [];
        
          for (const file of files) {
            if (file.type.includes("video"))
              continue
            // 1. Solicita URL pré-assinada
            const presignResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review/${reviewId}/pictures/presign`,
              {
                mimeType: file.type,
                fileSize: file.size,
              }
            );
        
            const { url, key } = presignResponse.data;
        
            // 2. Envia para o S3 diretamente
            await axios.put(url, file, {
              headers: {
                'Content-Type': file.type,
                'Content-Length': file.size,
              },
            });
        
            // 3. Armazena o key para informar ao backend depois
            uploadedKeys.push(key);
          }
        
          // 4. Confirma os arquivos enviados para o backend
          await axios.post(
            `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review/${reviewId}/pictures/confirm`,
            {
              updateToken,
              keys: uploadedKeys,
            }
          );
        }

        if (video != null) {
          const file = files.find((file) => file.type.includes("video"));
        
          // 1. Pede a URL pré-assinada
          const presignResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review/${reviewId}/video/presign`,
            {
              mimeType: file.type,
              fileSize: file.size,
            }
          );
        
          const { url, key } = presignResponse.data;
        
          // 2. Envia o vídeo direto para o S3
          await axios.put(url, file, {
            headers: {
              "Content-Type": file.type,
              "Content-Length": file.size,
            },
          });
        
          // 3. Confirma o envio no backend
          await axios.post(
            `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review/${reviewId}/video/confirm`,
            {
              updateToken: updateToken,
              key: key,
            }
          );
        }

        setActiveStep(2);
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleChangeMCAttributeValue = (evt) => {
    var attrId = evt.target.name;
    var attrValue = evt.target.value;

    var attribute = availableAttributes.find(x => x._id === attrId)
    var selectedValue = attribute.choices.findIndex(x => x === attrValue)

    if (evt.target.checked || evt.target.tagName.toUpperCase() === 'SELECT') {
      var attrValues = [...attributeValues];

      var elmIndex = attrValues.findIndex((attr) => {
        return attr._id === attrId;
      });

      if (elmIndex > -1 && selectedValue > -1) {
        attrValues[elmIndex].response = attrValue;
        attrValues[elmIndex].value = selectedValue;
      } else if (elmIndex == -1 && selectedValue > -1) {
        attrValues.push({
          _id: attrId,
          response: attrValue,
          value: selectedValue
        });
      } else if (elmIndex > -1 && selectedValue == -1) {
        attrValues.splice(elmIndex, 1)
      }

      setAttributeValues(attrValues)
    }
  };

  const handleTextAttributeValue = (evt) => {
    var attrId = evt.target.name;
    var attrValue = evt.target.value;

      var attrValues = [...attributeValues];

      var elmIndex = attrValues.findIndex((attr) => {
        return attr._id === attrId;
      });

      if (elmIndex > -1 && attrValue != "") {
        attrValues[elmIndex].response = attrValue;
        attrValues[elmIndex].value = 1;
      } else if (elmIndex == -1 && attrValue != "") {
        attrValues.push({
          _id: attrId,
          response: attrValue,
          value: 1
        });
      } else if (elmIndex > -1 && attrValue === "") {
        attrValues.splice(elmIndex, 1)
      }

      console.log(attrValues)

      setAttributeValues(attrValues)
  };

  const handleSubmitAttributes = async (evt) => {
    evt.preventDefault();

    window.gtag("event", "sendAttributes", {
      customer: product?.customer,
      sku: product?.sku,
    });

    setIsLoading(true);

    var body = {
      updateToken: updateTokenId,
      attributes: attributeValues,
    };

    await axios.post(
      `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review/${idReview}/attributes`,
      body
    );
    setIsLoading(false);
    setAttributesSent(true);

    if (modal) onHide();
  };

  // Função para salvar um atributo individual (modo progressivo)
  const saveAttribute = async (attributeId, attributeValue) => {
    try {
      setIsLoading(true);

      window.gtag("event", "saveProgressiveAttribute", {
        customer: product?.customer,
        sku: product?.sku,
        attributeId: attributeId,
      });

      const body = {
        updateToken: updateTokenId,
        attributes: [attributeValue],
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/${product?.customer}/${product?.sku}/review/${idReview}/attributes`,
        body
      );

      // Marca este atributo como salvo
      setSavedAttributes([...savedAttributes, attributeId]);
      
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Erro ao salvar atributo:', err);
      setIsLoading(false);
      return false;
    }
  };

  // Função para avançar para o próximo atributo (modo progressivo)
  const handleNextAttribute = async () => {
    const currentAttribute = availableAttributes[currentAttributeIndex];
    const currentValue = attributeValues.find(attr => attr._id === currentAttribute._id);

    // Se há valor, salva antes de avançar
    if (currentValue) {
      const saved = await saveAttribute(currentAttribute._id, currentValue);
      if (!saved) {
        // Se falhou ao salvar, não avança
        return;
      }
    }

    // Animação de transição
    setIsAttributeTransitioning(true);
    
    setTimeout(() => {
      if (currentAttributeIndex < availableAttributes.length - 1) {
        setCurrentAttributeIndex(currentAttributeIndex + 1);
      } else {
        // Último atributo, marca como concluído
        setAttributesSent(true);
      }
      setIsAttributeTransitioning(false);
    }, 400);
  };

  // Função para pular atributo sem salvar (modo progressivo)
  const handleSkipAttribute = () => {
    setIsAttributeTransitioning(true);
    
    setTimeout(() => {
      if (currentAttributeIndex < availableAttributes.length - 1) {
        setCurrentAttributeIndex(currentAttributeIndex + 1);
      } else {
        // Último atributo, marca como concluído
        setAttributesSent(true);
      }
      setIsAttributeTransitioning(false);
    }, 400);
  };

  const removeImage = (i) => {
    var filesInst = [...files];
    var imagesInst = [...images];

    filesInst.splice(i, 1);
    imagesInst.splice(i, 1);

    setFiles(filesInst);
    setImages(imagesInst);
    setUploadError(null);
  };

  const removeVideo = () => {
    const images = files.filter((f) => f.type.includes("image"));
    setFiles(images);
    setVideo(null);
    setUploadError(null);
  };

  const completenessPercent =
    (topicsCompleteness.filter((x) => !!x).length / topics.length) * 100;

  const uploadMediaEnable = () => {
    var mediaTypes = [];

    if (!customerInfo?.disablePictures) {
      mediaTypes.push("image/jpeg", "image/png", "image/webp");
    }

    if (customerInfo?.enableVideoReview) {
      mediaTypes.push(
        "video/mpeg",
        "video/ogg",
        "video/webm",
        "video/mp4",
        "video/quicktime"
      );
    }

    return mediaTypes.join(",");
  };

  const uploadText = () => {
    if (customerInfo?.settings?.collectionInterface?.labels?.upload) {
      return customerInfo.settings.collectionInterface.labels.upload;
    }
    if (!customerInfo?.disablePictures && customerInfo?.enableVideoReview) {
      return t('upload.both');
    } else if (customerInfo?.enableVideoReview) {
      return t('upload.video');
    } else {
      return t('upload.photo');
    }
  };

  return (
    <>
      {modal && (
        <div
          className={`rate-product-overlay ${show ? "show" : ""}`}
          onClick={() => {
            onHide();
          }}
        ></div>
      )}
      <div
        className={`rate-product ${modal ? "modal" : ""} ${
          show || !modal ? "show" : ""
        } ${product?.isStore ? "store" : ""}`}
      >
        {(modal || embedded) && (
          <>
            {activeStep === 1 && (
              <button
                className="back-button"
                onClick={() => {
                  onHide();
                }}
              >
                <img alt={t('alt.back')} src={backButton} />
              </button>
            )}
            {activeStep === 2 && (
              <button
                className="close-button"
                onClick={() => {
                  onHide();
                }}
              >
                <img alt={t('alt.close')} src={closeButton} />
              </button>
            )}
          </>
        )}
        <div className="step1">
          {product && activeStep === 1 && (
            <>
              <div className="product-details">
                {product.product?.image && (
                  <img
                    alt={t('alt.product_image', { name: product.product?.name })}
                    src={product.product?.image}
                  />
                )}
                {product.logo && (
                  <img alt={t('alt.product_image', { name: product.product?.name })} src={product.logo} />
                )}
                <div className="product-name">
                  {product.isStore &&
                    t('store_rating', { storeName: product.name })}

                  {!product.isStore &&
                    (product.product?.name ?? t('ref', { sku: product?.sku }))}
                </div>
              </div>

              <form className="form-review" onSubmit={handleSubmit}>
                <div className="label">
                  {interpolateText(
                    customerInfo?.settings?.collectionInterface?.labels?.rating,
                    interpolationVars,
                    categoryMap
                  ) || t('rating_label')}
                </div>
                <StarsRating
                  enabled={true}
                  ref={starsRef}
                  onChange={(rating) => {
                    setRating(rating);
                  }}
                />
                {!product.isStore && customerInfo?.settings?.collectionInterface?.enableRecommendation !== false && (
                  <>
                    <div className="label">
                      {interpolateText(
                        customerInfo?.settings?.collectionInterface?.labels?.recommendation,
                        interpolationVars,
                        categoryMap
                      ) || t('recommendation_label', { productName: product.product?.name ?? t('product') })}
                    </div>
                    <div className="options">
                      <label className="option">
                        <input
                          type="radio"
                          name="recommend"
                          checked={recommended}
                          onChange={(evt) => {
                            setRecommended(evt.target.checked);
                          }}
                        />{" "}
                        {t('recommend_yes')}
                      </label>
                      <label className="option">
                        <input
                          type="radio"
                          name="recommend"
                          checked={recommended === false}
                          onChange={(evt) => {
                            setRecommended(!evt.target.checked);
                          }}
                        />{" "}
                        {t('recommend_no')}
                      </label>
                    </div>
                  </>
                )}
                <div className="label">
                  {interpolateText(
                    customerInfo?.settings?.collectionInterface?.labels?.comment,
                    interpolationVars,
                    categoryMap
                  ) || (product.isStore
                    ? t('comment_label_store')
                    : t('comment_label_product', { productName: product.product?.name ?? t('product') }))}
                </div>
                {topics.length === 0 && (
                  <span className="description">
                    {interpolateText(
                      customerInfo?.settings?.collectionInterface?.hints?.comment,
                      interpolationVars,
                      categoryMap
                    ) || t('comment_tip')}
                    {(customerInfo?.settings?.collectionInterface?.requireComment || customerInfo?.settings?.requireText) && ` ${t('required_field')}`}
                  </span>
                )}

                <div className="text-container">
                  <textarea
                    className="review-text"
                    value={text}
                    onChange={(evt) => {
                      setText(evt.target.value);
                    }}
                  ></textarea>
                  {isLoadingCompleteness && (
                    <div className="loading-icon">
                      <Ripple />
                    </div>
                  )}
                  {topics.length > 0 && (
                    <div className="completeness">
                      <span className="completeness-label">
                        {t('completeness_label')}
                      </span>
                      <div className="completeness-topics">
                        {topics.map((topic, index) => {
                          return (
                            <div
                              key={index}
                              className={`completeness-topic ${
                                topicsCompleteness[index] ? "complete" : ""
                              }`}
                            >
                              {topic}
                            </div>
                          );
                        })}
                      </div>
                      <div className="completeness-progress">
                        <div
                          className={`indicator ${
                            completenessPercent > 99 ? "complete" : ""
                          }`}
                          style={{ width: completenessPercent + "%" }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {!preview && !product.orderId &&
                  customerInfo &&
                  customerInfo.enableUnauthenticatedReviews && (
                    <>
                      <div className="label">{t('name_label')}</div>
                      <input
                        type="text"
                        className="input-text"
                        value={name}
                        onChange={(evt) => {
                          setName(evt.target.value);
                        }}
                      />

                      <div className="label">{t('phone_label')}</div>
                      <InputMask
                        type="tel"
                        mask="99 99999-9999"
                        maskChar={null}
                        value={phone}
                        onChange={(evt) => {
                          setPhone(evt.target.value);
                        }}
                      />
                    </>
                  )}
                {!product.isStore &&
                  (!customerInfo ||
                    !customerInfo.disablePictures ||
                    customerInfo.enableVideoReview) && (
                    <>
                      <div className="label">{uploadText()}</div>
                      <span className="description">
                        {customerInfo?.settings?.collectionInterface?.hints?.upload || t('upload_tip')}
                      </span>

                      <ul className="pictures">
                        {images && images.length > 0 && (
                          <>
                            {images.map((image, i) => {
                              return (
                                <li key={i}>
                                  <img alt={t('alt.photo', { number: i + 1 })} src={image} />
                                  <span
                                    className="close"
                                    onClick={() => {
                                      removeImage(i);
                                    }}
                                  >
                                    X
                                  </span>
                                </li>
                              );
                            })}
                          </>
                        )}
                        {video != null && (
                          <li>
                            <video src={video} height="60" width="60" />
                            <span
                              className="close"
                              onClick={() => {
                                removeVideo();
                              }}
                            >
                              X
                            </span>
                          </li>
                        )}
                        <li className="add">
                          <button
                            className="add-files"
                            onClick={(evt) => {
                              evt.preventDefault();
                              document.getElementById("files").click();
                            }}
                          />
                        </li>
                      </ul>

                      {uploadError && (
                        <div className="error">{uploadError}</div>
                      )}

                      {((customerInfo?.settings?.consentText &&
                        images.length > 0) || customerInfo?.settings.explicitConsentAllReviews) && (
                          <div className="customer-consent-pictures">
                            <label>
                              {(customerInfo?.settings?.explicitConsent || customerInfo?.settings?.explicitConsentAllReviews) && (
                                <input
                                  type="checkbox"
                                  checked={explicitConsent}
                                  onChange={(e) => {
                                    setExplicitConsent(e.target.checked);
                                  }}
                                />
                              )}
                              {customerInfo?.settings?.consentText} 
                              <a
                                href={customerInfo?.settings?.consentLinkUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {customerInfo?.settings?.consentLinkText}
                              </a>
                            </label>
                          </div>
                        )}
                      <input
                        type="file"
                        className="file-upload"
                        id="files"
                        accept={uploadMediaEnable()}
                        onChange={previewFiles}
                      ></input>
                    </>
                  )}
                <button
                  type="submit"
                  className="button"
                  disabled={!isSendEnabled}
                >
                  {t('submit_review')}{" "}
                  {isLoading && <img alt={t('alt.loading')} src={loading} />}
                </button>
              </form>
            </>
          )}
        </div>
        <div className="step2">
          {activeStep === 2 && (
            <form className="form-attributes" onSubmit={handleSubmitAttributes}>
              <div className="thanks">
                <h4>{t('success_title')}</h4>
                <p>
                  {attributesSent
                    ? t('success_message_with_attributes')
                    : t('success_message')}
                </p>
              </div>
              {!attributesSent &&
                availableAttributes &&
                availableAttributes.length > 0 && (
                  <>
                    {/* Modo progressivo: um atributo por vez */}
                    {customerInfo?.settings?.collectionInterface?.progressiveAttributes ? (
                      <div className="progressive-attributes">
                        <div className="progress-indicator">
                          {t('attribute_progress', {
                            current: currentAttributeIndex + 1,
                            total: availableAttributes.length,
                            defaultValue: `${currentAttributeIndex + 1} de ${availableAttributes.length} atributos`
                          })}
                        </div>
                        
                        <div className={`attribute-container ${isAttributeTransitioning ? 'transitioning' : ''}`}>
                          {availableAttributes[currentAttributeIndex] && (
                            <div className="attribute progressive" key={availableAttributes[currentAttributeIndex]._id}>
                              <div className="label attribute-label">{availableAttributes[currentAttributeIndex].title}</div>
                              <div className="attribute-description">{availableAttributes[currentAttributeIndex].description}</div>
                              
                              {availableAttributes[currentAttributeIndex].type === "stars" && (
                                <StarsRating
                                  enabled={true}
                                  onChange={(rating) => {
                                    window.gtag("event", "rateAttribute", {
                                      customer: product?.customer,
                                      sku: product?.sku,
                                      attribute: availableAttributes[currentAttributeIndex]._id,
                                      rating: rating,
                                    });

                                    if (rating === 0 || !rating) return;

                                    var attrValues = [...attributeValues];
                                    var elmIndex = attrValues.findIndex(
                                      (attr) => attr._id === availableAttributes[currentAttributeIndex]._id
                                    );

                                    if (elmIndex > -1) {
                                      attrValues[elmIndex].value = rating;
                                    } else {
                                      attrValues.push({
                                        _id: availableAttributes[currentAttributeIndex]._id,
                                        value: rating,
                                      });
                                    }

                                    setAttributeValues(attrValues);
                                  }}
                                />
                              )}

                              {availableAttributes[currentAttributeIndex].type === "text" && (
                                <div className="text">
                                  <input 
                                    type="text" 
                                    value={attributeValues.find(x => x._id === availableAttributes[currentAttributeIndex]._id)?.response || ''} 
                                    name={availableAttributes[currentAttributeIndex]._id} 
                                    onChange={handleTextAttributeValue} 
                                  />
                                </div>
                              )}

                              {availableAttributes[currentAttributeIndex].type === "multiple-choice" && (
                                <div className="choices">
                                  {availableAttributes[currentAttributeIndex].choices.length < 5 ? (
                                    <>
                                      {availableAttributes[currentAttributeIndex].choices.map((item, index) => {
                                        var attrValues = [...attributeValues];
                                        var elmIndex = attrValues.findIndex(
                                          (attr) => attr._id === availableAttributes[currentAttributeIndex]._id
                                        );
                                        return (
                                          <label className="choice-label" key={index}>
                                            <input
                                              type="radio"
                                              name={availableAttributes[currentAttributeIndex]._id}
                                              checked={attrValues[elmIndex]?.response === item}
                                              value={item}
                                              onChange={handleChangeMCAttributeValue}
                                            />{" "}
                                            {item}
                                          </label>
                                        );
                                      })}
                                    </>
                                  ) : (
                                    <>
                                      <select
                                        name={availableAttributes[currentAttributeIndex]._id}
                                        value={attributeValues.find(x => x._id === availableAttributes[currentAttributeIndex]._id)?.response || ''}
                                        onChange={handleChangeMCAttributeValue}
                                      >
                                        <option value="">{t('select_option')}</option>
                                        {availableAttributes[currentAttributeIndex].choices.map((item, index) => {
                                          return (
                                            <option key={index}>{item}</option>
                                          );
                                        })}
                                      </select>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="progressive-buttons">
                          <button 
                            type="button" 
                            className="button-skip"
                            onClick={handleSkipAttribute}
                            disabled={isLoading}
                          >
                            {t('skip', { defaultValue: 'Pular' })}
                          </button>
                          <button 
                            type="button" 
                            className="button-next"
                            onClick={handleNextAttribute}
                            disabled={isLoading || !attributeValues.find(x => x._id === availableAttributes[currentAttributeIndex]._id)}
                          >
                            {isLoading && <img alt={t('alt.loading')} src={loading} />}
                            {currentAttributeIndex < availableAttributes.length - 1 
                              ? t('next', { defaultValue: 'Próximo' })
                              : t('finish', { defaultValue: 'Concluir' })
                            }
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Modo tradicional: todos os atributos de uma vez */
                      <>
                        <div className="attributes">
                          <label>{product?.isStore ? t('attributes_label_store') : t('attributes_label_product')}</label>
                          {availableAttributes.map((attribute) => {
                            return (
                              <div className="attribute" key={attribute._id}>
                                <div className="label attribute-label">{attribute.title}</div>
                                <div className="attribute-description">{attribute.description}</div>
                                {attribute.type === "stars" && (
                                  <StarsRating
                                    enabled={true}
                                    onChange={(rating) => {
                                      window.gtag("event", "rateAttribute", {
                                        customer: product?.customer,
                                        sku: product?.sku,
                                        attribute: attribute._id,
                                        rating: rating,
                                      });

                                      if (rating === 0 || !rating) return;

                                      var attrValues = [...attributeValues];

                                      var elmIndex = attrValues.findIndex(
                                        (attr) => {
                                          return attr._id === attribute._id;
                                        }
                                      );

                                      if (elmIndex > -1) {
                                        attrValues[elmIndex].value = rating;
                                      } else {
                                        attrValues.push({
                                          _id: attribute._id,
                                          value: rating,
                                        });
                                      }

                                      setAttributeValues(attrValues);
                                    }}
                                  />
                                )}

                                {attribute.type === "text" && (
                                  <div className="text"><input type="text" value={attribute.value} name={attribute._id} onChange={handleTextAttributeValue} /></div>
                                )}

                                {attribute.type === "multiple-choice" && (
                                  <div className="choices">
                                    {attribute.choices.length < 5 ? (
                                      <>
                                        {attribute.choices.map((item, index) => {
                                          var attrValues = [...attributeValues];
                                          var elmIndex = attrValues.findIndex(
                                            (attr) => {
                                              return attr._id === attribute._id;
                                            }
                                          );
                                          return (
                                            <label className="choice-label" key={index}>
                                              <input
                                                type="radio"
                                                name={attribute._id}
                                                checked={
                                                  attrValues[elmIndex]?.response ===
                                                  item
                                                }
                                                value={item}
                                                onChange={
                                                  handleChangeMCAttributeValue
                                                }
                                              />{" "}
                                              {item}
                                            </label>
                                          );
                                        })}
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          name={attribute._id}
                                          value={attributeValues.find(x => x._id === attribute._id)?.response}
                                          onChange={handleChangeMCAttributeValue}
                                        >
                                          <option value="">{t('select_option')}</option>
                                          {attribute.choices.map((item, index) => {
                                            return (
                                              <option key={index}>{item}</option>
                                            );
                                          })}
                                        </select>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <button type="submit" disabled={!isSendAttributesEnabled}>
                          {t('submit_attributes')}{" "}
                          {isLoading && <img alt={t('alt.loading')} src={loading} />}
                        </button>
                      </>
                    )}
                  </>
                )}
              
              {/* Botões de navegação */}
              {(attributesSent || !availableAttributes || availableAttributes.length === 0) && getNextItem && onNavigateNext && (
                <div className="navigation-buttons">
                  {(() => {
                    const nextItem = getNextItem();
                    if (nextItem) {
                      const isStoreReview = nextItem.isStore || nextItem.type === 'store';
                      const buttonText = isStoreReview 
                        ? t('rate_next_store', { defaultValue: 'Avaliar a loja' })
                        : t('rate_next_product', { defaultValue: 'Avaliar próximo produto' });
                      
                      return (
                        <button 
                          type="button"
                          className="button button-next"
                          onClick={() => {
                            onNavigateNext();
                          }}
                        >
                          {buttonText}
                        </button>
                      );
                    } else if (modal) {
                      return (
                        <button 
                          type="button"
                          className="button button-close"
                          onClick={() => {
                            onHideProps();
                          }}
                        >
                          {t('close', { defaultValue: 'Fechar' })}
                        </button>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
}